import {
  useSettings,
  type FontScale,
  type AnswerDetail,
  type AnswerTone,
} from "@/lib/settings";
import { ensureNotifyPermission, notifySupported } from "@/lib/notify";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/cn";
import { X } from "lucide-react";

const FONTS: { v: FontScale; label: string }[] = [
  { v: "base", label: "보통" },
  { v: "lg", label: "크게" },
  { v: "xl", label: "더 크게" },
];

const DETAILS: { v: AnswerDetail; label: string }[] = [
  { v: "", label: "표준" },
  { v: "brief", label: "간단" },
  { v: "detailed", label: "상세" },
];

const TONES: { v: AnswerTone; label: string }[] = [
  { v: "", label: "기본" },
  { v: "polite", label: "정중" },
  { v: "concise", label: "간결" },
  { v: "friendly", label: "친근" },
];

export default function SettingsPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [s, set] = useSettings();
  if (!open) return null;

  const onNotify = async () => {
    if (s.notify) {
      set({ notify: false });
      return;
    }
    const ok = await ensureNotifyPermission();
    set({ notify: ok });
    if (!ok) toast("브라우저에서 알림이 차단되어 있습니다. 사이트 설정에서 허용해 주세요.", "error");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="w-full max-w-sm rounded-xl bg-card p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <span className="font-semibold">설정</span>
          <button onClick={onClose} aria-label="닫기" className="rounded-md p-1 text-muted-foreground hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 text-sm">
          <div>
            <div className="mb-1.5 font-medium">답변 옵션</div>
            <div className="space-y-3 rounded-lg border border-border p-3">
              <div>
                <div className="mb-1.5 text-xs text-muted-foreground">답변 분량</div>
                <div className="flex gap-1.5">
                  {DETAILS.map((d) => (
                    <button
                      key={d.v || "std"}
                      onClick={() => set({ detail: d.v })}
                      className={cn(
                        "flex-1 rounded-lg border px-3 py-1.5",
                        s.detail === d.v
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-card hover:bg-muted",
                      )}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="mb-1.5 text-xs text-muted-foreground">말투</div>
                <div className="flex gap-1.5">
                  {TONES.map((t) => (
                    <button
                      key={t.v || "def"}
                      onClick={() => set({ tone: t.v })}
                      className={cn(
                        "flex-1 rounded-lg border px-2 py-1.5",
                        s.tone === t.v
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-card hover:bg-muted",
                      )}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <label className="flex items-center justify-between">
                <span>
                  영어로 답변
                  <span className="block text-xs text-muted-foreground">질문 언어와 무관하게 영어로</span>
                </span>
                <input
                  type="checkbox"
                  checked={s.english}
                  onChange={(e) => set({ english: e.target.checked })}
                  className="h-5 w-5 accent-[#EB4E26]"
                />
              </label>
            </div>
          </div>

          <div>
            <div className="mb-1.5 text-muted-foreground">글자 크기</div>
            <div className="flex gap-1.5">
              {FONTS.map((f) => (
                <button
                  key={f.v}
                  onClick={() => set({ fontScale: f.v })}
                  className={cn(
                    "flex-1 rounded-lg border px-3 py-2",
                    s.fontScale === f.v
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-card hover:bg-muted",
                  )}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <label className="flex items-center justify-between">
            <span>고대비 모드</span>
            <input
              type="checkbox"
              checked={s.highContrast}
              onChange={(e) => set({ highContrast: e.target.checked })}
              className="h-5 w-5 accent-[#EB4E26]"
            />
          </label>

          {notifySupported() && (
            <label className="flex items-center justify-between">
              <span>
                브라우저 알림
                <span className="block text-xs text-muted-foreground">공지·작업 완료 알림</span>
              </span>
              <input
                type="checkbox"
                checked={s.notify}
                onChange={onNotify}
                className="h-5 w-5 accent-[#EB4E26]"
              />
            </label>
          )}
        </div>
      </div>
    </div>
  );
}
